import React, { useState, useRef } from "react";
import {
  Box,
  Grid,
  Typography,
  Button,
  Container,
  IconButton,
} from "@mui/material";
import { motion, useScroll, useTransform, useSpring } from "framer-motion";
import CircleIcon from "@mui/icons-material/Circle";
import { useNavigate } from "react-router-dom";

import mbsq1 from "../assets/images/moon_bar/moon-main.jpg";
import mbsq2 from "../assets/images/moon_bar/sqr/PXL_20251120_122337263.jpg";
import mbsq3 from "../assets/images/moon_bar/sqr/PXL_20251120_122545006.jpg";
import mbsq4 from "../assets/images/moon_bar/sqr/PXL_20251120_123748142.NIGHT-EDIT.jpg";
import mbhr5 from "../assets/images/moon_bar/hory/PXL_20251120_123052606.NIGHT-EDIT.jpg";
import mbsq6 from "../assets/images/moon_bar/sqr/PXL_20251120_124015085-EDIT.jpg";
import mbsq7 from "../assets/images/moon_bar/sqr/PXL_20251120_124536884.jpg";

import bosq1 from "../assets/images/bothra_office/bothra-main.jpg";
import bosq2 from "../assets/images/bothra_office/hory/PXL_20251108_074207423-EDIT.jpg";
import bosq3 from "../assets/images/bothra_office/hory/PXL_20251108_075551986-EDIT.jpg";
import bosq4 from "../assets/images/bothra_office/hory/PXL_20251108_075200654-EDIT.jpg";
import bosq5 from "../assets/images/bothra_office/hory/PXL_20251108_075522578-EDIT.jpg";

import aosq1 from "../assets/images/apollo_office/apollo-main.jpg";
import aosq2 from "../assets/images/apollo_office/hory/PXL_20251108_092944258.jpg";
import aosq3 from "../assets/images/apollo_office/hory/PXL_20251108_093015878.jpg";
import aosq4 from "../assets/images/apollo_office/hory/PXL_20251108_093104363.jpg";
import aosq5 from "../assets/images/apollo_office/hory/PXL_20251108_094002808.jpg";
import aosq6 from "../assets/images/apollo_office/hory/PXL_20251108_094024538-EDIT.jpg";
import aosq7 from "../assets/images/apollo_office/hory/PXL_20251108_094149597.jpg";
import aosq8 from "../assets/images/apollo_office/hory/PXL_20251108_094301843-EDIT.jpg";
import aosq9 from "../assets/images/apollo_office/hory/PXL_20251108_094318324.jpg";

const SHARED_IMAGES = [mbsq1, mbsq2, mbsq3, mbsq4, mbhr5, mbsq6, mbsq7];
const SHARED_IMAGES2 = [bosq1, bosq2, bosq3, bosq4, bosq5];
const SHARED_IMAGES3 = [
  aosq1,
  aosq2,
  aosq3,
  aosq4,
  aosq5,
  aosq6,
  aosq7,
  aosq8,
  aosq9,
];

const projectsData = [
  {
    title: "Moon Bar & Kitchen",
    location: "VIP Road, Visakhapatnam",
    images: SHARED_IMAGES,
    link: "/project/moonbar",
  },
  {
    title: "Bothra Shipping Corporate Office",
    location: "Sampath Vinayak Temple Road, Visakhapatnam",
    images: SHARED_IMAGES2,
    link: "/project/bothra",
  },
  {
    title: "Apollo Shipping Office",
    location: "Suryabagh, Visakhapatnam",
    images: SHARED_IMAGES3,
    link: "/project/apollo",
  },
];

const ProjectSection = ({ project }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isFading, setIsFading] = useState(false);
  const sectionRef = useRef(null);
  const navigate = useNavigate();
  const swipeConfidenceThreshold = 80;

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start 90%", "end 10%"],
  });

  const smoothProg = useSpring(scrollYProgress, {
    stiffness: 80,
    damping: 20,
    mass: 0.25,
  });

  const imgY = useTransform(smoothProg, [0, 1], ["-25px", "25px"]);

  const handleDotClick = (index) => {
    if (index === activeIndex) return;
    setIsFading(true);
    setTimeout(() => {
      setActiveIndex(index);
      setIsFading(false);
    }, 140);
  };

  const paginate = (direction) => {
    setIsFading(true);

    setTimeout(() => {
      setActiveIndex((prev) => {
        if (direction === 1) {
          return (prev + 1) % project.images.length;
        } else {
          return (prev - 1 + project.images.length) % project.images.length;
        }
      });
      setIsFading(false);
    }, 120);
  };

  return (
    <Box
      ref={sectionRef}
      sx={{
        py: { xs: 5, sm: 7, md: 9 },
        px: { xs: 2.5, sm: 5, md: 7 },
        display: "flex",
        justifyContent: "center",
      }}
    >
      <Grid
        container
        spacing={{ xs: 4, sm: 5, md: 7 }}
        alignItems="center"
        justifyContent="space-between"
        sx={{
          maxWidth: "1200px",
          width: "100%",
          flexDirection: { xs: "column", sm: "column", md: "row" },
        }}
      >
        {/* LEFT TEXT */}
        <Grid item xs={12} md={6}>
          <Box sx={{ textAlign: { xs: "center", md: "left" } }}>
            <Typography
              sx={{
                fontFamily: "'Playfair Display', serif",
                fontWeight: 600,
                color: "#b1976b",
                mb: 1,
                fontSize: {
                  xs: "1.7rem",
                  sm: "2rem",
                  md: "2.5rem",
                },
              }}
            >
              {project.title}
            </Typography>

            <Typography
              sx={{
                fontFamily: "'Poppins', sans-serif",
                fontWeight: 600,
                mb: 2,
                color: "#000",
                letterSpacing: 1,
                fontSize: { xs: "0.8rem", sm: "0.9rem", md: "1rem" },
              }}
            >
              {project.location?.toUpperCase?.()}
            </Typography>

            <Button
              variant="outlined"
              sx={{
                borderColor: "#b1976b",
                color: "#b1976b",
                fontFamily: "'Poppins', sans-serif",
                px: { xs: 3, sm: 4 },
                py: { xs: 1, sm: 1.1 },
                borderWidth: "1.5px",
                "&:hover": {
                  backgroundColor: "#b1976b",
                  color: "#fff",
                  borderColor: "#b1976b",
                },
              }}
              onClick={() => navigate(project.link)}
            >
              EXPLORE PROJECT
            </Button>
          </Box>
        </Grid>

        {/* RIGHT IMAGE + DOTS */}
        <Grid
          item
          xs={12}
          md={6}
          sx={{
            // force a real gap on desktop for EVERY project
            pl: { md: 4, lg: 5 },
            display: "flex",
            justifyContent: { xs: "center", md: "flex-start" },
          }}
        >
          <Box
            sx={{
              position: "relative",
              display: "flex",
              flexDirection: "column",
              alignItems: { xs: "center", md: "flex-start" },
              // important: prevent the image from collapsing if text gets wide
              minWidth: { md: 420, lg: 460 },
              width: { xs: "100%", sm: "92%", md: "auto" },
            }}
          >
            {/* IMAGE FRAME */}
            <Box
              sx={{
                overflow: "hidden",
                position: "relative",
                borderRadius: "12px",
                width: {
                  xs: "100%",
                  sm: "92%",
                  md: 440,
                  lg: 500,
                },
                height: {
                  xs: 230,
                  sm: 300,
                  md: 330,
                  lg: 380,
                },
                boxShadow: "0 8px 20px rgba(0,0,0,0.1)",
                backgroundColor: "#ddd",
              }}
            >
              <motion.div
                drag="x"
                dragConstraints={{ left: 0, right: 0 }}
                dragElastic={0.2}
                onDragEnd={(e, info) => {
                  if (info.offset.x < -swipeConfidenceThreshold) {
                    paginate(1); // swipe left → next
                  } else if (info.offset.x > swipeConfidenceThreshold) {
                    paginate(-1); // swipe right → previous
                  }
                }}
                style={{
                  width: "100%",
                  height: "100%",
                  cursor: "grab",
                }}
              >
                <motion.img
                  key={activeIndex}
                  src={project.images[activeIndex]}
                  alt={project.title}
                  initial={{ x: 40, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  exit={{ x: -40, opacity: 0 }}
                  transition={{ duration: 0.35, ease: "easeOut" }}
                  style={{
                    y: imgY,
                    width: "100%",
                    height: "130%",
                    objectFit: "cover",
                    willChange: "transform",
                    pointerEvents: "none",
                    userSelect: "none",
                  }}
                />
              </motion.div>
            </Box>

            {/* DOTS */}
            <Box
              sx={{
                display: "flex",
                justifyContent: { xs: "center", md: "flex-start" },
                mt: 2,
                gap: 1.5,
                width: {
                  xs: "100%",
                  sm: "92%",
                  md: 440,
                  lg: 500,
                },
              }}
            >
              {project.images.map((_, index) => (
                <IconButton
                  key={index}
                  onClick={() => handleDotClick(index)}
                  size="small"
                  sx={{
                    color:
                      index === activeIndex ? "#b1976b" : "rgba(0,0,0,0.3)",
                    transition: "color 0.3s ease",
                  }}
                >
                  <CircleIcon sx={{ fontSize: 10 }} />
                </IconButton>
              ))}
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

const Projects = () => {
  return (
    <Box sx={{ backgroundColor: "#fff", width: "100%" }}>
      <Container maxWidth="xl" disableGutters>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: 4,
            px: { xs: 3, md: 8 },
            py: { xs: 6, md: 10 },
            backgroundColor: "#fff",
            alignItems: { xs: "center", md: "flex-start" },
          }}
        >
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              color: "#b2925a",
              fontFamily: "serif",
              flex: 1,
              textAlign: { xs: "center", md: "left" },
            }}
          >
            Our Projects
          </Typography>

          <Box sx={{ flex: 2 }}>
            <Typography
              variant="body1"
              sx={{ color: "#333", mb: 2, textAlign: "justify" }}
            >
              Beyond aesthetics, true luxury lies in the details of execution.
              This collection features our most complex challenges—ranging from
              bespoke furniture fabrication and heritage restoration to turnkey
              corporate fit-outs.
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: "#333", textAlign: "justify" }}
            >
              These projects highlight ARC Studioz not just as designers, but as
              technical partners who engineer the "nervous systems" of a
              building—the MEP, IT, and structural elements—ensuring that every
              space works as flawlessly as it looks.
            </Typography>
          </Box>
        </Box>
        {projectsData.map((proj, idx) => (
          <ProjectSection key={idx} project={proj} />
        ))}
      </Container>
    </Box>
  );
};

export default Projects;
